// deps
import { useState, useCallback, useEffect, useContext, useRef } from "react";

// internal-deps
import { Result, Ok, Err } from "@swiggy-private/js-fp";
import { useMountedRef } from "@swiggy-private/core-ui";

// interfaces
import { GetAllCardsResponse } from "@payment-client/interfaces/CardApi";

import {
    ApiContext,
    useSessionStorage,
    AnalyticsContext,
    ConfigContext,
    CheckoutContext,
} from "@swiggy-private/checkout-core";
import { ApiResponse } from "@payment-client/interfaces/Api";

// apis
import { getAllCardsPublic } from "@payment-client/apis/publicCard";

type ResultGetAllCards = Result<GetAllCardsResponse, Error | ApiResponse>;
type ResultData = Result<
    {
        getAllCardsResponse: GetAllCardsResponse;
    },
    Error | ApiResponse
>;

export interface GetAllCardsState {
    loading: boolean;
    data?: ResultData;
}

interface GetAllCardsCacheValue {
    getAllCardsResponse: GetAllCardsResponse;
}

async function getAllCardsData(apiHost: string): Promise<ResultGetAllCards> {
    const getAllCardsPromise = getAllCardsPublic(apiHost)
        .then(data => {
            if (!data) {
                throw new Error("Received empty data from get all cards");
            }

            return Ok(data);
        })
        .catch(err => {
            // TODO: Event for get all cards failute
            return Err(err) as ResultGetAllCards;
        });

    return getAllCardsPromise;
}

const usePaymentModesState = (): [GetAllCardsState, () => void] => {
    const apiContextValue = useContext(ApiContext);
    const { checkoutState } = useContext(CheckoutContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { cacheGetAllCardsData } = useContext(ConfigContext);
    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    const [dataInCache, saveDataInCache] = useSessionStorage<GetAllCardsCacheValue | null>("get_all_cards", null);
    const [state, setState] = useState<GetAllCardsState>({ loading: true });

    const apiHost = apiContextValue.host.unwrap();
    const canCacheAllCardsData = cacheGetAllCardsData?.unwrapOr(true);

    const reset = useCallback((): void => {
        saveDataInCache(null);
        setState({ loading: true });
    }, [saveDataInCache]);

    useEffect(() => {
        if (dataInCache && state.loading) {
            setState({
                data: Ok({
                    getAllCardsResponse: dataInCache.getAllCardsResponse,
                }),
                loading: false,
            });
            return;
        }

        if (!state.loading || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;

        getAllCardsData(apiHost)
            .then(allCardsData => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    const data = allCardsData.map(getAllCardsResponse => {
                        if (canCacheAllCardsData) {
                            saveDataInCache({
                                getAllCardsResponse,
                            });
                        }

                        return {
                            getAllCardsResponse,
                        };
                    });

                    setState({ loading: false, data });
                }
            })
            .catch(err => {
                apiCallRef.current = false;
                if (isMountedRef.current) {
                    canCacheAllCardsData && saveDataInCache(null);
                    setState({ loading: false, data: Err(err) });
                }
            });
    }, [
        state.loading,
        dataInCache,
        apiHost,
        isMountedRef,
        saveDataInCache,
        impressionEvent,
        canCacheAllCardsData,
        checkoutState.type,
    ]);

    return [state, reset];
};

export default usePaymentModesState;
